import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { Router } from '@angular/router';
import { SimpleModalService } from '@looorent/ngx-simple-modal';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { LoginServiceBase } from './login.service.base';
import { LoginModalComponent } from './login-modal/login-modal.component';
import { LocalStorageService } from '../Services/local-storage.service';


@Injectable({
  providedIn: 'root'
})
export class LoginService implements LoginServiceBase {
  @Output()
  public LoginStatusChanged = new EventEmitter<any>();
  @Output()
  public OrganizationChanged = new EventEmitter<any>();
  @Output()
  public OrganizationDeleted = new EventEmitter<any>();
  
  public RequiresPasswordReset: boolean = false;
  
  constructor(private httpClient: HttpClient, private router: Router, private modalService: SimpleModalService, private localStorageService: LocalStorageService) {
    let cachedUserData = localStorageService.Get(this.localStorageKey);
    let cachedSessionToken = localStorageService.GetSession(this.sessionStorageKey);
    if (cachedUserData?.SID != null) {
      // Migrate existing localStorage data so deployed users lose the persistent SID.
      cachedSessionToken = cachedSessionToken ?? cachedUserData.SID;
      this.SaveUserDataWithoutToken(cachedUserData);
    }
    if (cachedUserData != null && cachedSessionToken != null && cachedSessionToken != "") {
      this.SaveSessionToken(cachedSessionToken);
      this.UserData = {
        ...cachedUserData,
        SID: cachedSessionToken
      };
      this.LoginStatusChanged.emit(true);
      this.OrganizationChanged.emit(this.UserData.OrganizationId);
    }
  }

  private localStorageKey = "UserData";
  private sessionStorageKey = "UserDataSID";

  private UserData = {
    UserId: "00000000-0000-0000-0000-000000000000",
    FirstName: "anonymous user",
    LastName: "last name",
    SID: "",
    OrganizationId: "",
    Permissions: []
  };


  public IsLoggedIn() {
    return this.UserData.SID != "";
  }
  public LoggedInName() {
    return this.UserData.LastName != null ? this.UserData.FirstName + " " + this.UserData.LastName : this.UserData.FirstName;
  }
  public LoggedInNameChanged(firstName: string, lastName: string) {
    this.UserData.FirstName = firstName;
    this.UserData.LastName = lastName;
    this.SaveUserDataWithoutToken(this.UserData);
  }
  public CurrentToken() {
    //console.log("SID " + this.UserData.SID)
    return this.UserData.SID;
  }
  public CurrentOrganizationId() {
    return this.UserData.OrganizationId;
  }
  public UserId() {
    return this.UserData.UserId;
  }

  public async DoLogin(username: string, password: string): Promise<any> {
    console.log("Attempting login for " + username);
    try {
      let data: any = await lastValueFrom(this.httpClient.post<any>(environment.serverBaseUrl + "/auth/adminlogin", { username: username, password: password }));
      if (data.SID != null) {
        this.UserData = data;
        this.SaveUserDataWithoutToken(data);
        this.SaveSessionToken(data.SID);
        console.log("Login Success for " + username);
        this.RequiresPasswordReset = data.PasswordResetRequired;
        this.LoginStatusChanged.emit(true);
        return { Success: true };
      }
    } catch (error: any) {
      return { Success: false, Error: error.error.Error };
    }
    return { Success: false };
  }
  public async DoLogout(): Promise<void> {
    // GuidePoint M1 (FP-1246): revoke the session server-side; clearing local state
    // alone leaves the SID token valid and replayable.
    if (this.CurrentToken() != "") {
      let headers = new HttpHeaders().set("authorization", "Bearer " + this.CurrentToken());
      try {
        await lastValueFrom(this.httpClient.delete(environment.serverBaseUrl + "/auth/logout", { headers }));
      } catch (error: any) {
        console.log("Server-side logout request failed", error);
      }
    }
    this.UserData = {
      UserId: "00000000-0000-0000-0000-000000000000",
      SID: "",
      FirstName: "anonymous user",
      LastName: "last name",
      OrganizationId: "",
      Permissions: []
    };
    this.localStorageService.Clear(this.localStorageKey);
    this.ClearSessionToken();
    // Clear all stored data against with the current user
    this.localStorageService.ClearAll();
    this.LoginStatusChanged.emit(false);
    await this.router.navigate(['login']);
  }

  public DoOrganizationSwitch(id: string) {
    let headers = new HttpHeaders().set("authorization", "Bearer " + this.CurrentToken());
    this.httpClient.post<any>(environment.serverBaseUrl + "/auth/currentorganization", { OrganizationId: id }, { headers })
      .subscribe(result => {
        this.UserData.SID = result.SID;
        this.UserData.OrganizationId = id;
        this.SaveUserDataWithoutToken(this.UserData);
        this.SaveSessionToken(result.SID);
        this.OrganizationChanged.emit(this.UserData.OrganizationId);
        this.router.navigate(['']);
      });
  }

  public NewTestOrganization(name: string) {
    let headers = new HttpHeaders().set("authorization", "Bearer " + this.CurrentToken());
    this.httpClient.post<any>(environment.serverBaseUrl + "/organization/newtestorg", {UserId: this.UserData.UserId, Name: name} , { headers })
      .subscribe(result => {
        this.DoOrganizationSwitch(result.Id);
      });
  }

  public DeleteNewTestOrganization(name: string) {
    let headers = new HttpHeaders().set("authorization", "Bearer " + this.CurrentToken());
    this.httpClient.post<any>(environment.serverBaseUrl + "/organization/deleteorg", {Name: name} , { headers })
      .subscribe(result => {
        this.OrganizationDeleted.emit(true);
      });
  }

  private LoginModal: any = null;
  public NotifyAuthExpired() {
    console.log('Auth service notified of expired auth. Showing login');
    this.UserData.SID = "";
    this.ClearSessionToken();

    if (this.router.url.startsWith('/login') || this.router.url.startsWith('/reset-password')) {
      console.log('Skipping login modal - user is on authentication page');
      return;
    }

    if (this.LoginModal != null) {
      //already a login modal present
      return;
    }
    this.LoginModal = this.modalService.addModal(LoginModalComponent, {})
      .subscribe((result: any) => {
        this.LoginModal = null;
        if (result != null) {

        }
      });
  }

  public async RequestPasswordReset(email: string): Promise<any> {
    console.log("Requesting password reset for " + email);
    try {
      let data: any = await lastValueFrom(this.httpClient.post<any>(environment.serverBaseUrl + "/auth/resetpassword", { Email: email }));
      console.log("Password reset request for " + email + " returned " + data);
      return { Success: true, Data: data };
    } catch (error: any) {
      return { Success: false, Error: error.error.Error };
    }
  }

  public async ResetPassword(email: string, newPassword: string, code: string): Promise<any> {
    console.log("Resetting password for " + email);
    try {
      await lastValueFrom(this.httpClient.post<any>(environment.serverBaseUrl + "/auth/resetpassword/" + code, { Email: email, NewPassword: newPassword }));
      return { Success: true };
    } catch (error: any) {
      return { Success: false, Error: error.error.Error };
    }
  }

  public DebugBreakAuthToken() {
    this.UserData.SID = "----";
    this.SaveSessionToken(this.UserData.SID);
  }

  private SaveUserDataWithoutToken(userData: any) {
    let userDataWithoutToken = {
      UserId: userData.UserId,
      FirstName: userData.FirstName,
      LastName: userData.LastName,
      OrganizationId: userData.OrganizationId,
      Permissions: userData.Permissions
    };
    this.localStorageService.Save(this.localStorageKey, userDataWithoutToken);
  }

  private SaveSessionToken(sessionToken: string) {
    if (sessionToken == null || sessionToken == "") {
      this.ClearSessionToken();
      return;
    }
    this.localStorageService.SaveSession(this.sessionStorageKey, sessionToken);
  }

  private ClearSessionToken() {
    this.localStorageService.ClearSession(this.sessionStorageKey);
  }

}
